import { Edit } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButtonLink } from '~/components/button';
import { Link } from '~/components/link';
import type { NetworkEventTypes__EventType as EventType } from '../../queries/list.generated';
import { Menu } from '../components';

export const humanizeDuration = (
  duration: string,
  suffix = 'before',
  t: (key: string) => string,
  locale: string,
) => {
  const d = Duration.fromISO(duration).rescale();

  // If duration is zero
  if (d.toMillis() === 0) {
    return t('pages.event_types.duration.zero_minutes');
  }

  // Set locale for Luxon
  const localizedDuration = d.reconfigure({ locale: locale.startsWith('es') ? 'es' : 'en' });
  const humanized = localizedDuration.toHuman();
  const translatedSuffix = t(`pages.event_types.duration.${suffix}`);
  return `${humanized} ${translatedSuffix}`;
};

export const useColumns = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo<GridColDef<EventType>[]>(
    () => [
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <img alt="" src={row.iconFile?.uri} style={{ width: '25px', height: 'auto' }} />
            <span>{row.name}</span>
          </Box>
        ),
        minWidth: isSmallAndUp ? 180 : 0,
      },
      {
        field: 'eventCount',
        headerName: t('common.tabs.events'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            color="primary"
            component={Link}
            label={row.eventCount}
            onClick={(event) => event.stopPropagation()}
            to={`${row.id}/events`}
          />
        ),
        minWidth: 100,
      },
      {
        field: 'contentStartTime',
        headerName: t('pages.event_types.columns.content_start_time'),
        renderCell: ({ row }) => humanizeDuration(row.contentStartTime, 'before', t, i18n.language),
        sortable: false,
        minWidth: 225,
      },
      {
        field: 'contentStopTime',
        headerName: t('pages.event_types.columns.content_stop_time'),
        renderCell: ({ row }) => humanizeDuration(row.contentStopTime, 'after', t, i18n.language),
        sortable: false,
        minWidth: 225,
      },
      {
        field: 'sponsorStartTime',
        headerName: t('pages.event_types.columns.sponsor_start_time'),
        renderCell: ({ row }) => humanizeDuration(row.sponsorStartTime, 'before', t, i18n.language),
        sortable: false,
        minWidth: 225,
      },
      {
        field: 'sponsorStopTime',
        headerName: t('pages.event_types.columns.sponsor_stop_time'),
        renderCell: ({ row }) => humanizeDuration(row.sponsorStopTime, 'after', t, i18n.language),
        sortable: false,
        minWidth: 225,
      },
      {
        field: 'actions',
        type: 'actions',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }) => [
          <ActionButtonLink
            key={`edit-${row.id}`}
            title={t('common.actions.edit')}
            Icon={Edit}
            to={`${row.id}`}
          />,
          <Menu key={`more-action-${row.id}`} eventType={row} />,
        ],
      },
    ],
    [isSmallAndUp, t, i18n.language],
  );
};
